/**
 * azure-functions/shared/billing/planDefaultData.cjs
 *
 * Canonical implementation. Required directly by
 * azure-functions/shared/billing/planDefaults.js (pricing fallback for
 * invoicing) and re-exported for the frontend by
 * src/config/planDefaults.js — there is exactly one copy of this data;
 * do not fork it again.
 *
 * Default plan definitions based on prices.html feature comparison.
 * These defaults are used when no custom plan definitions have been saved
 * in the database. The super-admin can override any of these values
 * via the Plans tab in the admin portal.
 *
 * Structure:
 *   plans     — keyed by plan ID (free, standard, pro, enterprise)
 *   limits    — numeric caps (0 = not available, -1 = unlimited)
 *   modules   — boolean access per nav module key
 *   features  — boolean flags for sub-features within modules
 */

const PLAN_IDS = ['free', 'standard', 'pro', 'enterprise'];

const PLAN_META = {
  free: { label: 'Free', color: '#6ee7b7', price: 0, monthlyPrice: 0, priceLabel: 'forever free' },
  standard: {
    label: 'Standard',
    color: '#94a3b8',
    price: 7000,
    monthlyPrice: 700,
    priceLabel: '€7,000 /yr',
  },
  pro: {
    label: 'Pro',
    color: '#38bdf8',
    price: 12000,
    monthlyPrice: 1400,
    priceLabel: '€12,000 /yr',
  },
  enterprise: {
    label: 'Enterprise',
    color: '#a78bfa',
    price: 17000,
    monthlyPrice: 1700,
    priceLabel: '€17,000 /yr',
  },
};

/**
 * Limit definitions — each entry describes a numeric limit that can vary per plan.
 * value of -1 means unlimited.
 */
const LIMIT_DEFS = [
  { key: 'users', label: 'Users' },
  { key: 'frameworks', label: 'Frameworks' },
  { key: 'documents', label: 'Documents' },
  { key: 'risks', label: 'Risks' },
  { key: 'vendors', label: 'Vendors' },
  { key: 'employees', label: 'Employees' },
  { key: 'gapAssessments', label: 'Gap Assessments' },
];

/**
 * Module definitions — maps to navConfig.js keys.
 * Each entry can be toggled on/off per plan.
 */
const MODULE_DEFS = [
  // Core Work
  { key: 'dashboard', label: 'Dashboard', group: 'Core Work' },
  { key: 'executive', label: 'Executive View', group: 'Core Work' },
  { key: 'nextsteps', label: 'Next Steps', group: 'Core Work' },
  { key: 'guides', label: 'Guides', group: 'Core Work' },
  { key: 'tasks', label: 'Tasks', group: 'Core Work' },
  { key: 'calendar', label: 'Calendar', group: 'Core Work' },
  // Compliance
  { key: 'controls', label: 'Controls', group: 'Compliance' },
  { key: 'documents', label: 'Documents', group: 'Compliance' },
  { key: 'privacy', label: 'Privacy', group: 'Compliance' },
  { key: 'gap', label: 'Assessments', group: 'Compliance' },
  { key: 'management-review', label: 'Mgt Review', group: 'Compliance' },
  // Risk & Assurance
  { key: 'risks', label: 'Risks & Opps', group: 'Risk & Assurance' },
  { key: 'threats', label: 'Threats', group: 'Risk & Assurance' },
  { key: 'vulnerabilities', label: 'Vulnerabilities', group: 'Risk & Assurance' },
  { key: 'findings', label: 'Findings', group: 'Risk & Assurance' },
  { key: 'incidents', label: 'Incidents', group: 'Risk & Assurance' },
  { key: 'vendors', label: 'Vendors', group: 'Risk & Assurance' },
  { key: 'employees', label: 'Employees', group: 'Risk & Assurance' },
  { key: 'assets', label: 'Assets', group: 'Risk & Assurance' },
  { key: 'bia', label: 'BIA', group: 'Risk & Assurance' },
  { key: 'customers', label: 'Customers', group: 'Risk & Assurance' },
  { key: 'delivery-agreements', label: 'Agreements', group: 'Risk & Assurance' },
  { key: 'subcontractors', label: 'Subcontractors', group: 'Risk & Assurance' },
  { key: 'agreements-register', label: 'Contracts', group: 'Risk & Assurance' },
  // System
  { key: 'integrations', label: 'Integrations', group: 'System' },
  { key: 'auditor', label: 'Auditor View', group: 'System' },
  { key: 'email-breach-monitoring', label: 'Breach Monitor', group: 'System' },
  // ISMS improvement measurement (msr 2.7, GH #7130): the internal Metric
  // Explorer today, the improvement views in Phase 4.
  { key: 'improvement', label: 'Improvement', group: 'System' },
  // Policy-driven workflows (PDW): paid plans only, founder decision D8 = B.
  { key: 'workflows', label: 'Workflows', group: 'System' },
  { key: 'ai-governance', label: 'AI Governance', group: 'Risk & Assurance' },
];

/**
 * Feature definitions — sub-features within modules that can be toggled per plan.
 */
const FEATURE_DEFS = [
  { key: 'evidenceCollection', label: 'Evidence Collection (Tasks)', group: 'Tasks & Documents' },
  { key: 'documentApproval', label: 'Document Approval Workflow', group: 'Tasks & Documents' },
  { key: 'documentTemplates', label: 'Document Templates', group: 'Tasks & Documents' },
  { key: 'riskOpportunities', label: 'Risk Opportunities', group: 'Risk Management' },
  {
    key: 'advancedRiskMode',
    label: 'Advanced Risk Mode (full governance)',
    group: 'Risk Management',
  },
  { key: 'vendorContractTracking', label: 'Vendor Contract Expiry Tracking', group: 'Vendors' },
  { key: 'vendorAiEvaluation', label: 'AI Vendor Risk Evaluation', group: 'Vendors' },
  // RAI D6 = A (RISK-B-013): AI-assisted risk assessment, Pro and Enterprise.
  { key: 'riskAi', label: 'AI-assisted risk assessment', group: 'Risk Management' },
  { key: 'employeeEvaluations', label: 'Employee Evaluations & Docs', group: 'Employees' },
  { key: 'employeePortal', label: 'Employee Self-Service Portal', group: 'Employees' },
  { key: 'aiThreatIntelligence', label: 'AI Threat Intelligence', group: 'AI' },
  { key: 'aiDocumentAnalysis', label: 'AI Document Analysis', group: 'AI' },
  { key: 'customFrameworks', label: 'Custom Frameworks', group: 'Frameworks' },
  { key: 'engineJobs', label: 'Engine (Background Jobs)', group: 'Automation' },
  { key: 'dashboardSparkline', label: 'Dashboard Sparkline History', group: 'Core Platform' },
  { key: 'dataExport', label: 'Data Export', group: 'Platform' },
  { key: 'activityLog', label: 'Activity Log', group: 'Platform' },
];

/**
 * Default plan definitions — mirrors prices.html feature comparison table.
 */
/**
 * Default Shieldwall program configuration — founding-member discount program.
 * Shieldwall offers tiered, locked-for-life discounts to early customers.
 * Stored separately from plan definitions in the DB (row: 'shieldwall-config').
 */
const DEFAULT_SHIELDWALL = {
  active: false,
  label: 'Shieldwall',
  tagline: 'Limited to the first 25 companies',
  discounts: {
    standard: { percent: 50 },
    pro: { percent: 40 },
    enterprise: { percent: 25 },
  },
};

const DEFAULT_PLAN_DEFINITIONS = {
  free: {
    pricing: {
      yearlyPrice: 0,
      monthlyPrice: 0,
    },
    limits: {
      users: 1,
      frameworks: 1,
      documents: 5,
      risks: 10,
      vendors: 0,
      employees: 0,
      gapAssessments: 1,
    },
    modules: {
      dashboard: true,
      executive: false,
      nextsteps: true,
      guides: true,
      tasks: true,
      calendar: true,
      controls: true,
      documents: true,
      privacy: false,
      gap: true,
      'management-review': false,
      risks: true,
      threats: false,
      vulnerabilities: false,
      findings: false,
      incidents: false,
      vendors: false,
      employees: false,
      assets: false,
      bia: false,
      customers: false,
      'delivery-agreements': false,
      subcontractors: false,
      'agreements-register': false,
      integrations: false,
      auditor: false,
      'email-breach-monitoring': false,
      improvement: false,
      workflows: false,
      'ai-governance': false,
    },
    features: {
      evidenceCollection: false,
      documentApproval: false,
      documentTemplates: false,
      riskOpportunities: false,
      advancedRiskMode: false,
      vendorContractTracking: false,
      vendorAiEvaluation: false,
      riskAi: false,
      employeeEvaluations: false,
      employeePortal: false,
      aiThreatIntelligence: false,
      aiDocumentAnalysis: false,
      customFrameworks: false,
      engineJobs: false,
      dashboardSparkline: false,
      dataExport: true,
      activityLog: true,
    },
  },

  standard: {
    pricing: {
      yearlyPrice: 7000,
      monthlyPrice: 700,
    },
    limits: {
      users: 5,
      frameworks: 2,
      documents: -1,
      risks: -1,
      vendors: 10,
      employees: 25,
      gapAssessments: -1,
    },
    modules: {
      dashboard: true,
      executive: true,
      nextsteps: true,
      guides: true,
      tasks: true,
      calendar: true,
      controls: true,
      documents: true,
      privacy: true,
      gap: true,
      'management-review': true,
      risks: true,
      threats: false,
      vulnerabilities: true,
      findings: true,
      incidents: true,
      vendors: true,
      employees: true,
      assets: true,
      bia: true,
      customers: true,
      'delivery-agreements': true,
      subcontractors: true,
      'agreements-register': true,
      integrations: true,
      auditor: true,
      'email-breach-monitoring': true,
      improvement: true,
      workflows: true,
      'ai-governance': true,
    },
    features: {
      evidenceCollection: true,
      documentApproval: true,
      documentTemplates: false,
      riskOpportunities: false,
      advancedRiskMode: false,
      vendorContractTracking: false,
      vendorAiEvaluation: false,
      riskAi: false,
      employeeEvaluations: false,
      employeePortal: false,
      aiThreatIntelligence: false,
      aiDocumentAnalysis: false,
      customFrameworks: false,
      engineJobs: true,
      dashboardSparkline: false,
      dataExport: true,
      activityLog: true,
    },
  },

  pro: {
    pricing: {
      yearlyPrice: 12000,
      monthlyPrice: 1400,
    },
    limits: {
      users: 20,
      frameworks: 5,
      documents: -1,
      risks: -1,
      vendors: 50,
      employees: 250,
      gapAssessments: -1,
    },
    modules: {
      dashboard: true,
      executive: true,
      nextsteps: true,
      guides: true,
      tasks: true,
      calendar: true,
      controls: true,
      documents: true,
      privacy: true,
      gap: true,
      'management-review': true,
      risks: true,
      threats: true,
      vulnerabilities: true,
      findings: true,
      incidents: true,
      vendors: true,
      employees: true,
      assets: true,
      bia: true,
      customers: true,
      'delivery-agreements': true,
      subcontractors: true,
      'agreements-register': true,
      integrations: true,
      auditor: true,
      'email-breach-monitoring': true,
      improvement: true,
      workflows: true,
      'ai-governance': true,
    },
    features: {
      evidenceCollection: true,
      documentApproval: true,
      documentTemplates: true,
      riskOpportunities: true,
      advancedRiskMode: true,
      vendorContractTracking: true,
      vendorAiEvaluation: true,
      riskAi: true,
      employeeEvaluations: true,
      employeePortal: true,
      aiThreatIntelligence: true,
      aiDocumentAnalysis: true,
      customFrameworks: true,
      engineJobs: true,
      dashboardSparkline: true,
      dataExport: true,
      activityLog: true,
    },
  },

  enterprise: {
    pricing: {
      yearlyPrice: 17000,
      monthlyPrice: 1700,
    },
    limits: {
      users: -1,
      frameworks: -1,
      documents: -1,
      risks: -1,
      vendors: -1,
      employees: -1,
      gapAssessments: -1,
    },
    modules: {
      dashboard: true,
      executive: true,
      nextsteps: true,
      guides: true,
      tasks: true,
      calendar: true,
      controls: true,
      documents: true,
      privacy: true,
      gap: true,
      'management-review': true,
      risks: true,
      threats: true,
      vulnerabilities: true,
      findings: true,
      incidents: true,
      vendors: true,
      employees: true,
      assets: true,
      bia: true,
      customers: true,
      'delivery-agreements': true,
      subcontractors: true,
      'agreements-register': true,
      integrations: true,
      auditor: true,
      'email-breach-monitoring': true,
      improvement: true,
      workflows: true,
      'ai-governance': true,
    },
    features: {
      evidenceCollection: true,
      documentApproval: true,
      documentTemplates: true,
      riskOpportunities: true,
      advancedRiskMode: true,
      vendorContractTracking: true,
      vendorAiEvaluation: true,
      riskAi: true,
      employeeEvaluations: true,
      employeePortal: true,
      aiThreatIntelligence: true,
      aiDocumentAnalysis: true,
      customFrameworks: true,
      engineJobs: true,
      dashboardSparkline: true,
      dataExport: true,
      activityLog: true,
    },
  },
};

module.exports = {
  PLAN_IDS,
  PLAN_META,
  LIMIT_DEFS,
  MODULE_DEFS,
  FEATURE_DEFS,
  DEFAULT_SHIELDWALL,
  DEFAULT_PLAN_DEFINITIONS,
};
